import { Link } from 'react-router-dom'
import DocLayout from '../components/docs/DocLayout'
import CopyButton from '../components/docs/CopyButton'
import Icon from '../components/docs/Icon'
import Cased from '@/components/ui/Cased'

const html = `<html lang="en" data-theme="light" data-mission="america">
  <head>
    <link rel="stylesheet" href="/mds/tokens.css" />
    <link rel="stylesheet" href="/mds/fonts.css" />
    <link rel="stylesheet" href="/mds/themes.css" />
    <link rel="stylesheet" href="/mds/components.css" />
  </head>
  <body>
    <button class="mds-button mds-button--primary">Start an application</button>
  </body>`

const css = `/* MDS components use their mds-* classes as documented. Your own
   components read the same semantic roles, so they follow light, dark
   and every mission theme. */
.permit-status {
  background: var(--mds-surface);
  color: var(--mds-ink);
  border-left: 4px solid var(--mds-accent);
  border-radius: var(--mds-radius-card);
  padding: var(--mds-space-4) var(--mds-space-6);
  font-family: var(--mds-font-sans);
}

.permit-status__title {
  font-family: var(--mds-font-display);
  font-size: var(--mds-text-h3);
  color: var(--mds-ink);
}`

const create = 'npx missiondesign-create my-site'
const install = `npm install missiondesign

/* In your CSS entry point: typefaces, tokens, themes and components */
@import 'missiondesign/mds.css';`

const starters = [
  ['vite-react', 'Vite and React'],
  ['next', 'Next.js'],
  ['html', 'Plain HTML'],
] as const

const steps = [
  'Start with the task',
  'Start from a starter',
  'Add the stylesheets',
  'Choose a theme and a mission',
  'Build with components and roles',
  'Give your agent the context',
  'Verify the complete experience',
]

export default function GettingStartedPage() {
  return (
    <DocLayout
      eyebrow="Get Started"
      title="A stronger starting point."
      intro="MDS is a set of plain CSS files, typed definitions and documented components. Install one package or add four stylesheets, choose a mission and build with semantic roles."
    >
      <section className="doc-section doc-step">
        <span className="doc-step__number">01</span>
        <h2><Cased>{steps[0]}</Cased></h2>
        <p>
          Describe who needs to do what, why it matters and what a successful
          outcome looks like. Note the operating context: a public service, a
          field workflow or an internal mission workspace.
        </p>
        <blockquote className="doc-callout">
          <p className="mds-eyebrow">Example mission brief</p>
          <p>
            “A veteran needs to check the status of a claim, understand what
            happens next and upload a missing document without calling.”
          </p>
        </blockquote>
      </section>
      <section className="doc-section doc-step">
        <span className="doc-step__number">02</span>
        <h2><Cased>{steps[1]}</Cased></h2>
        <p>
          A starter is a working site with the stylesheets, the typefaces and every{' '}
          <Link to="/templates">page template</Link>, themed for the agency you choose: Vite and React, Next.js, or plain
          HTML with no build step. Create one from the command line:
        </p>
        <pre className="mds-code doc-code-block" tabIndex={0} aria-label="Create a site">
          <code>{create}</code>
        </pre>
        <CopyButton text={create} label="Copy command" />
        <p>It asks for the starter, the mission and the template that opens first. Or download one:</p>
        <ul className="doc-starters">
          {starters.map(([id, name]) => (
            <li key={id}>
              <a className="mds-button mds-button--secondary mds-button--sm" href={`/downloads/starters/mds-${id}-starter.zip`} download>
                <Icon name="download" size={16} />
                {name}
              </a>
            </li>
          ))}
        </ul>
        <p>Or add MDS to an existing project, as the next steps show.</p>
      </section>
      <section className="doc-section doc-step">
        <span className="doc-step__number">03</span>
        <h2><Cased>{steps[2]}</Cased></h2>
        <p>
          Install <a href="https://www.npmjs.com/package/missiondesign">missiondesign</a> from npm. Its{' '}
          <code>mds.css</code> brings the self-hosted typefaces, the tokens, every mission theme and the component
          styles in one import. For React, add{' '}
          <a href="https://www.npmjs.com/package/missiondesign-react">missiondesign-react</a>.
        </p>
        <pre className="mds-code doc-code-block" tabIndex={0} aria-label="Install from npm">
          <code>{install}</code>
        </pre>
        <CopyButton text={install} label="Copy install" />
        <p>
          Without a package manager, download{' '}
          <a href="/mds/tokens.css" download>
            tokens.css
          </a>
          , <a href="/mds/fonts.css">fonts.css</a>,{' '}
          <a href="/mds/themes.css">themes.css</a> and{' '}
          <a href="/mds/components.css">components.css</a> into your project
          and link them in that order. Tokens define the type, space, shape,
          motion and default American color roles; fonts adds the typeface
          switches; themes adds every mission; components styles the{' '}
          <code>mds-*</code> classes.
        </p>
        <pre className="mds-code" tabIndex={0}>
          <code>{html}</code>
        </pre>
        <p>
          Install the typefaces from Google Fonts through Fontsource, so they
          are served from your own origin:{' '}
          <code>@fontsource/instrument-serif</code>,{' '}
          <code>@fontsource-variable/inter</code> and{' '}
          <code>@fontsource-variable/geist-mono</code>.
        </p>
      </section>
      <section className="doc-section doc-step">
        <span className="doc-step__number">04</span>
        <h2><Cased>{steps[3]}</Cased></h2>
        <p>
          Two attributes on <code>&lt;html&gt;</code> select the look.{' '}
          <code>data-theme</code> is <code>light</code> or <code>dark</code>;
          set it from the visitor&apos;s saved choice, or from{' '}
          <code>prefers-color-scheme</code> before they choose.{' '}
          <code>data-mission</code> names an agency theme, or{' '}
          <code>america</code> for red, white and blue.
        </p>
        <p>
          A single agency only needs its own file, such as{' '}
          <a href="/mds/themes/usda.css">themes/usda.css</a>, in place of{' '}
          themes.css.
        </p>
        <Link to="/missions" className="mds-button mds-button--secondary">
          Find your mission <Icon name="arrow" size={16} />
        </Link>
      </section>
      <section className="doc-section doc-step">
        <span className="doc-step__number">05</span>
        <h2><Cased>{steps[4]}</Cased></h2>
        <p>
          Use the <code>mds-*</code> component classes as documented, and read
          color, type and space from the semantic roles in your own CSS. Never
          hard-code a hex value: a role adapts to the theme and the mission.
        </p>
        <div className="doc-code-block">
          <pre className="mds-code" tabIndex={0}>
            <code>{css}</code>
          </pre>
          <CopyButton text={css} label="Copy CSS example" />
        </div>
        <div className="mds-cluster">
          <Link to="/components" className="mds-button mds-button--secondary">
            Browse components <Icon name="arrow" size={16} />
          </Link>
          <Link to="/patterns" className="mds-button mds-button--ghost">
            Explore the pattern library <Icon name="arrow" size={16} />
          </Link>
        </div>
      </section>
      <section className="doc-section doc-step">
        <span className="doc-step__number">06</span>
        <h2><Cased>{steps[5]}</Cased></h2>
        <p>
          Load the <a href="/mds/manifest.json">resource manifest</a> and{' '}
          <a href="/mds/agents.md" download>
            agent instructions
          </a>
          . Pair them with your mission brief and the selected pattern contract.
        </p>
        <Link to="/agents" className="mds-button mds-button--ghost">
          Read the agent guide <Icon name="arrow" size={16} />
        </Link>
      </section>
      <section className="doc-section doc-step">
        <span className="doc-step__number">07</span>
        <h2><Cased>{steps[6]}</Cased></h2>
        <ul className="pat-checks">
          {[
            'Check the pattern’s acceptance criteria, including recovery states.',
            'Complete the task with a keyboard and test the focus order.',
            'Check light and dark themes and your mission theme.',
            'Run automated accessibility checks and a 320px reflow check.',
            'Test enlarged text and assistive technology with real users.',
          ].map((item) => (
            <li key={item}>
              <Icon name="check" size={17} />
              {item}
            </li>
          ))}
        </ul>
        <p>
          Data handling, security and agency-specific requirements are reviewed
          for each application in its own context.
        </p>
      </section>
    </DocLayout>
  )
}
